import React, { useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import styled from "styled-components";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import moment from "moment";

import { getRefundReport } from "../../../apis/customTemplate";
import { toastify } from "../../../helperFunctions/toastify";
import DateInput from "../../SharedComponents/DateInput";
import { setTableSearch } from "../../../redux/slices/commonSlice";

import {
  customDateSort,
  downloadCsv,
  formatDate,
  getInvoiceId,
  getRefundInvoiceId,
} from "../../../helperFunctions/utils";

const FilterWrapper = styled.div`
  label {
    color: inherit;
  }

  .btn-wrapper {
    align-items: center;
    display: flex;
    margin-top: 20px;
  }
`;

function RefundReportView() {
  const numberFormatter = Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const buttonRef = useRef(null);
  const pathname = location.pathname;
  const fieldPathname = pathname.replace("/", "");

  const { tableSearch } = useSelector((state) => state.commonData);

  const [filteredData, setFilteredData] = useState([]);
  const [refundList, setRefundList] = useState([]);
  const [startDate, setStartDate] = useState(
    tableSearch[fieldPathname]?.refundL?.startDate
      ? tableSearch[fieldPathname]?.refundL?.startDate
      : `${new Date().getFullYear()}-01-01`
  );
  const [endDate, setEndDate] = useState(
    tableSearch[fieldPathname]?.refundL?.endDate
      ? tableSearch[fieldPathname]?.refundL?.endDate
      : `${new Date().getFullYear()}-12-29`
  );
  const [refetch, setRefetch] = useState(false);

  const onReduxFilter = (valueStartDate, valueEndDate) => {
    dispatch(
      setTableSearch({
        ...tableSearch,
        [fieldPathname]: {
          ...tableSearch[fieldPathname],
          refundL: {
            startDate: valueStartDate,
            endDate: valueEndDate,
          },
        },
      })
    );
  };

  useEffect(() => {
    setStartDate(
      tableSearch[fieldPathname]?.refundL?.startDate
        ? tableSearch[fieldPathname]?.refundL?.startDate
        : `${new Date().getFullYear()}-01-01`
    );
    setEndDate(
      tableSearch[fieldPathname]?.refundL?.endDate
        ? tableSearch[fieldPathname]?.refundL?.endDate
        : `${new Date().getFullYear()}-12-29`
    );

    if (
      buttonRef.current &&
      (tableSearch[fieldPathname]?.refundL?.startDate ||
        tableSearch[fieldPathname]?.refundL?.endDate)
    ) {
      setTimeout(() => {
        if (buttonRef.current) {
          buttonRef.current.click();
        }
      }, [1000]);
    }
  }, []);

  useEffect(() => {
    getRefundReport({
      start_date: startDate,
      end_date: endDate,
    }).then((res) => {
      console.log("res?.result?.data", res?.result?.data);
      if (res?.result?.data) {
        setRefundList(res.result.data);
        setFilteredData(res.result.data);
      }
    });
  }, [refetch]);
  console.log("filteredData", filteredData, "refundList", refundList);

  // eslint-disable-next-line no-sparse-arrays
  const columns = [
    {
      name: "Date",
      selector: (row) => row.created_at,
      sortable: true,
      sortFunction: (a, b) => customDateSort(a, b, "created_at"),
      cell: (row) => formatDate(row.created_at),
    },
    {
      name: "Invoice ID",
      selector: (row) => row.linkedPayment?.invoiceNo || 0,
      cell: (row) => getInvoiceId(row?.linkedPayment?.invoiceNo),
    },
    {
      name: "Refund ID",
      selector: (row) => row.invoiceNo || 0,
      cell: (row) => getRefundInvoiceId(row?.invoiceNo),
    },
    {
      name: "Patient Name",
      selector: (row) => row.patient?.patientProfile?.fullName,
    },
    {
      name: "Original Amount",
      selector: (row) => row.linkedPayment?.gst,
      sortable: true,
      cell: (row) => numberFormatter.format(row.linkedPayment?.gst),
    },
    {
      name: "Refund Amount",
      selector: (row) => row.gst,
      cell: (row) => numberFormatter.format(row.gst),
    },
    {
      name: "Payed",
      selector: (row) => {
        const lastPayment = row.paymentRecord?.[row.paymentRecord.length - 1];
        return lastPayment?.payed || 0;
      },
      cell: (row) => {
        const lastPayment = row.paymentRecord?.[row.paymentRecord.length - 1];
        return numberFormatter.format(lastPayment?.payed || 0);
      },
    },
  ];

  const data = filteredData?.map((e, index) => {
    return {
      ...e,
      index,
      key: index,
    };
  });

  const customStyles = {
    headCells: {
      style: {
        justifyContent: "center",
      },
    },
    cells: {
      style: {
        justifyContent: "center",
      },
    },
  };

  const conditionalRowStyles = [
    {
      when: (row) => row,
      style: {
        borderWidth: "0px",
      },
    },
    {
      when: (row) => row.index % 2 === 1,
      style: {
        backgroundColor: "#f8f8f8",
        borderWidth: "0px",
      },
    },
  ];

  const exportCsv = () => {
    const csvData = refundList.map((row) => {
      return {
        Date: formatDate(row.created_at),
        "Patient Name": row.patient?.patientProfile?.fullName,
        "Invoice ID": getInvoiceId(row?.linkedPayment?.invoiceNo),
        "Refund ID": getRefundInvoiceId(row?.invoiceNo),
        "Original Amount": numberFormatter.format(row.linkedPayment?.gst),
        "Refunded Amount": numberFormatter.format(row.gst),
      };
    });

    downloadCsv(csvData, "Refund_Report");
  };

  return (
    <div className="p-0">
      <div
        className="mainHeading"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            marginTop: "18px",
          }}
        >
          <FilterWrapper className="d-flex">
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={startDate}
                label="Start Date"
                onChange={(e) => {
                  setStartDate(e);
                }}
              />
            </div>
            <div className="me-3" style={{ width: "260px" }}>
              <DateInput
                value={endDate}
                label="End Date"
                onChange={(e) => {
                  setEndDate(e);
                }}
              />
            </div>
            <div className="btn-wrapper">
              <Button
                ref={buttonRef}
                className="addItem me-3"
                onClick={() => {
                  if (endDate < startDate) {
                    toastify("End date is less than start date", "error");
                    return;
                  }
                  onReduxFilter(startDate, endDate);
                  setRefetch(!refetch);
                }}
              >
                Search
              </Button>
              <Button className="addItem" onClick={() => exportCsv()}>
                Export
              </Button>
            </div>
          </FilterWrapper>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data || []}
        pagination
        customStyles={customStyles}
        conditionalRowStyles={conditionalRowStyles}
      />
    </div>
  );
}

export default RefundReportView;
